import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon} from '@heroicons/react/24/outline';
import {batchStatusClass, formatBatchDate, formatBatchDuration, formatBatchStatus} from './automationBatchFormatting';

export default function BatchAutomationHistory({apiUrl, requestHeaders, page, onPageChange, onSelect}) {
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        let ignore = false;
        setLoading(true);
        setError('');
        setHistory(null);
        axios.get(`${apiUrl}/automation/batches`, {headers: requestHeaders, params: {page, size: 20}})
            .then(response => {
                if (ignore) return;
                if (page > 0 && page >= response.data.totalPages) {
                    onPageChange(Math.max(0, response.data.totalPages - 1));
                } else {
                    setHistory(response.data);
                }
            })
            .catch(requestError => {
                if (!ignore) setError(requestError.response?.data?.error || 'Could not load batch history.');
            })
            .finally(() => {if (!ignore) setLoading(false);});
        return () => {ignore = true;};
    }, [apiUrl, requestHeaders, page, onPageChange, revision]);

    return <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Batch history</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Newest first · {history?.totalElements ?? '…'} saved batches. Open a batch to see its profiles and item results.</p>
            </div>
            <button type="button" disabled={loading} onClick={() => setRevision(value => value + 1)} className="mv-secondary-button">
                <ArrowPathIcon className="h-4 w-4" /> Refresh history
            </button>
        </div>
        {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-panda-pink/30 dark:bg-panda-pink/10 dark:text-panda-pink">{error}</p>}
        {loading && <p role="status" className="py-8 text-center text-sm text-gray-600 dark:text-gray-400">Loading batch history…</p>}
        {!loading && history?.batches?.length === 0 && <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center dark:border-dark-700">
            <ClockIcon className="mx-auto mb-3 h-7 w-7 text-gray-400" />
            <p className="font-medium text-gray-900 dark:text-gray-100">No batches yet</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Your automation results will appear here after you start a batch.</p>
        </div>}
        <div className="space-y-3">
            {(history?.batches || []).map(batch => <button type="button" key={batch.batchId}
                aria-label={`View batch ${batch.batchId}`} onClick={() => onSelect(batch.batchId)}
                className="w-full rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-cyan-400 hover:bg-cyan-50/30 focus-visible:outline-cyan-700 dark:border-dark-700 dark:bg-dark-850 dark:hover:border-panda-cyan/40 dark:hover:bg-panda-cyan/5">
                <div className="flex items-center justify-between gap-3">
                    <div><p className="font-semibold text-gray-900 dark:text-gray-100">Batch #{batch.batchId}</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{batch.profileCount} profiles · {batch.itemCount} items · Created {formatBatchDate(batch.createdAt)}</p></div>
                    <div className="flex shrink-0 items-center gap-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${batchStatusClass(batch.status)}`}>{formatBatchStatus(batch.status)}</span>
                        <ChevronRightIcon className="h-4 w-4 text-gray-500" /></div>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                    <div><span className="block text-xs text-gray-500 dark:text-gray-400">Started</span><span className="text-gray-800 dark:text-gray-200">{formatBatchDate(batch.startedAt)}</span></div>
                    <div><span className="block text-xs text-gray-500 dark:text-gray-400">Finished</span><span className="text-gray-800 dark:text-gray-200">{formatBatchDate(batch.completedAt)}</span></div>
                    <div><span className="block text-xs text-gray-500 dark:text-gray-400">Duration</span><span className="font-medium text-gray-800 dark:text-gray-200">{formatBatchDuration(batch.startedAt, batch.completedAt)}</span></div>
                </div>
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{batch.successfulItemCount} completed · {batch.failedItemCount} failed{batch.skippedItemCount ? ` · ${batch.skippedItemCount} skipped` : ''}</p>
            </button>)}
        </div>
        {history?.totalPages > 1 && <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4 dark:border-dark-700">
            <button type="button" className="mv-secondary-button" disabled={loading || page === 0} onClick={() => onPageChange(page - 1)}><ChevronLeftIcon className="h-4 w-4" /> Previous</button>
            <span className="text-sm text-gray-600 dark:text-gray-400">Page {page + 1} of {history.totalPages}</span>
            <button type="button" className="mv-secondary-button" disabled={loading || page + 1 >= history.totalPages} onClick={() => onPageChange(page + 1)}>Next <ChevronRightIcon className="h-4 w-4" /></button>
        </div>}
    </div>;
}
